/* eslint-disable react/prop-types, react/no-multi-comp, consistent-return, no-restricted-syntax */
import qs from 'qs';
import { converge, merge, path } from 'ramda';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { atom, useSetRecoilState } from 'recoil';
import { setObservableConfig } from 'recompose';
import xstreamConfig from 'recompose/xstreamObservableConfig';

setObservableConfig(xstreamConfig);

// /////////////////////////////////////////////////////////////////////////////
// View Utils
// /////////////////////////////////////////////////////////////////////////////
export const isAsyncViewLoadedState = atom({
  key: 'isAsyncViewLoaded',
  default: false,
});

function LoadedComponent({ children }: { children: React.ReactNode }) {
  const setIsAsyncViewLoaded = useSetRecoilState(isAsyncViewLoadedState);

  useEffect(() => {
    setIsAsyncViewLoaded(true);

    return function cleanup() {
      setIsAsyncViewLoaded(false);
    };
  }, []);

  return <>{children}</>;
}

const Placeholder = (props: any) => <div />;

export const asyncComponent = (getComponent: any) => {
  return class AsyncComponent extends React.Component {
    state = { Component: Placeholder };

    async componentWillMount() {
      try {
        const Component = await getComponent(this.props);

        this.setState(() => ({
          Component: (props: any) => (
            <LoadedComponent>
              <Component {...props} />
            </LoadedComponent>
          ),
        }));
      } catch (err) {
        console.error(err);

        const ErrorComponent = (props: any) => <Error {...props} err={err} />;

        this.setState(() => ({ Component: ErrorComponent }));
      }
    }

    componentWillUnmount() {
      this.setState = () => ({
        Component: Placeholder,
      });
    }

    render() {
      const StateComponent = this.state.Component;
      return <StateComponent {...this.props} />;
    }
  };
};

// @todo: move this into views
export function Error({ err }: { err: { message: string } }) {
  return (
    <p>FAIL {err.message || "Sorry, it looks like we're have a problem :("}</p>
  );
}

const callMethod = (p: any, o: any) => (...args: any) => path(p, o)(...args);
const mapLocation = (location: Location) => ({
  ...location,
  query: qs.parse(location.search.substr(1)),
});

const mapDynamicProps = (props: any) => ({
  ...props,
  children: undefined,
  location: mapLocation(props.location),
  params: props.match.params,
  notifications: props.children,
});

const REF_CACHE = {};
const mapStaticProps = merge({
  notify: callMethod(['toasts', 'add'], REF_CACHE),
});

export const evolveProps = converge(merge, [mapStaticProps, mapDynamicProps]);

export type RenderChildrenProps = {
  children?: ReactNode;
};

export function renderChildren<TChildrenProps extends unknown>(
  { children }: RenderChildrenProps,
  childrenProps: TChildrenProps = {} as TChildrenProps,
) {
  const resolvedChildren = (typeof children === 'function'
    ? children(childrenProps)
    : children) as ReactElement | ReactElement[];

  return resolvedChildren;
}
